import {
  AccountSetupOnboardingCoachmark,
  buildOnboardingSteps,
  completeOnboarding,
  CreateShipmentOnboardingCoachmark,
  getOnboardingStorageState,
  InstallApplicationOnboardingCoachmark,
  markOnboardingShown,
  OnboardingStep,
  SafariInstallationInstructions,
  SupportOperatorOnboardingCoachmark,
  useBlockingInterfaces,
  useOnboardingLaunchConditions,
  useOnboardingSession,
  usePwaInstallation
} from '@/features/Onboarding';
import { MainPageOnboardingProps } from './types';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MainPageOnboarding = (props: MainPageOnboardingProps) => {
  const {
    hasOrganization,
    isAuthenticated,
    isEmailVerified,
    isProfileError,
    isProfileLoading,
    isProfileSuccess,
    isShipmentsError,
    isShipmentsLoading,
    isShipmentsSuccess,
    shipmentCount,
    userId
  } = props;
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [attemptSteps, setAttemptSteps] = useState<readonly OnboardingStep[] | null>(null);
  const [isInstallationInstructionsOpen, setIsInstallationInstructionsOpen] = useState(false);
  const [storageState, setStorageState] = useState(() => getOnboardingStorageState(userId));
  const location = useLocation();
  const {
    isHandledInSession,
    markHandledInSession
  } = useOnboardingSession();
  const {
    isBlocked
  } = useBlockingInterfaces();
  const {
    scenario: pwaInstallationScenario
  } = usePwaInstallation();

  const closeAttempt = useCallback((): void => {
    setAttemptSteps(null);
    setActiveStepIndex(0);
    setIsInstallationInstructionsOpen(false);
  }, []);

  const completeAttempt = useCallback((): void => {
    setStorageState(completeOnboarding(userId));
    markHandledInSession();
    closeAttempt();
  }, [closeAttempt, markHandledInSession, userId]);

  const onShipmentDetected = useCallback((): void => {
    setStorageState(getOnboardingStorageState(userId));
    markHandledInSession();
    closeAttempt();
  }, [closeAttempt, markHandledInSession, userId]);

  const {
    isLaunchReady
  } = useOnboardingLaunchConditions({
    hasOrganization,
    isAuthenticated,
    isBlockingInterfaceOpen: isBlocked,
    isHandledInSession,
    isProfileError,
    isProfileLoading,
    isProfileSuccess,
    isShipmentsError,
    isShipmentsLoading,
    isShipmentsSuccess,
    pathname: location.pathname,
    shipmentCount,
    storageState,
    userId
  }, onShipmentDetected);

  useEffect(() => {
    setActiveStepIndex(0);
    setAttemptSteps(null);
    setIsInstallationInstructionsOpen(false);
    setStorageState(getOnboardingStorageState(userId));
  }, [userId]);

  useEffect(() => {
    if (!isLaunchReady || attemptSteps) return;

    const steps = buildOnboardingSteps({
      hasOrganization,
      isEmailVerified,
      pwaInstallationScenario
    });

    markHandledInSession();
    setStorageState(markOnboardingShown(userId));
    setActiveStepIndex(0);
    setAttemptSteps(steps);
  }, [
    attemptSteps,
    hasOrganization,
    isEmailVerified,
    isLaunchReady,
    markHandledInSession,
    pwaInstallationScenario,
    userId
  ]);

  useEffect(() => {
    if (!isBlocked || !attemptSteps) return;

    markHandledInSession();
    closeAttempt();
  }, [attemptSteps, closeAttempt, isBlocked, markHandledInSession]);

  if (!attemptSteps) return null;

  const activeStep = attemptSteps[activeStepIndex];
  const stepNumber = activeStepIndex + 1;
  const totalSteps = attemptSteps.length;

  const onBack = (): void => {
    setActiveStepIndex((currentIndex) => Math.max(0, currentIndex - 1));
  };
  const onNext = (): void => {
    if (activeStepIndex === totalSteps - 1) {
      completeAttempt();
      return;
    }

    setActiveStepIndex((currentIndex) => currentIndex + 1);
  };

  if (activeStep.id === 'ACCOUNT_SETUP') {
    return (
      <AccountSetupOnboardingCoachmark
        isOpen
        onClose={closeAttempt}
        onNext={onNext}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        variant={activeStep.variant}
      />
    );
  }

  if (activeStep.id === 'CREATE_SHIPMENT') {
    return (
      <CreateShipmentOnboardingCoachmark
        isOpen
        onBack={activeStepIndex > 0 ? onBack : undefined}
        onClose={closeAttempt}
        onNext={onNext}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
      />
    );
  }

  if (activeStep.id === 'SUPPORT_OPERATOR') {
    return (
      <SupportOperatorOnboardingCoachmark
        isOpen
        onBack={onBack}
        onClose={closeAttempt}
        onNext={onNext}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
      />
    );
  }

  return (
    <>
      <InstallApplicationOnboardingCoachmark
        isOpen={!isInstallationInstructionsOpen}
        onClose={closeAttempt}
        onComplete={completeAttempt}
        onOpenInstructions={() => setIsInstallationInstructionsOpen(true)}
        scenario={activeStep.scenario}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
      />
      {activeStep.scenario === 'NATIVE' ? null : (
        <SafariInstallationInstructions
          isOpen={isInstallationInstructionsOpen}
          onClose={() => setIsInstallationInstructionsOpen(false)}
          onComplete={completeAttempt}
          scenario={activeStep.scenario}
        />
      )}
    </>
  );
};

export default MainPageOnboarding;